/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { Session } from '../entities/Session';
import { Log } from '../logging';
import { PlaybackContentType } from '../playback/enums';
import { ITivoSubscription, TivoSubscriptionModel } from '../tivo';
import { SessionState } from './enums';
import {
    IpLinearStreamingSessionImpl,
    NdvrStreamingSessionImpl,
    SocuStreamingSessionImpl,
} from './impls';
import {
    IPlaybackError,
    ISessionCreateModel,
    ISessionManager,
    IStreamingSession,
} from './interfaces';

/* eslint-disable class-methods-use-this */
export class SessionManager implements ISessionManager {
    private static TAG: string = 'SessionManager';

    private mStreamingSession: IStreamingSession;

    private oTivoSubscription: ITivoSubscription<SessionState>;

    private timeoutId: any;

    public constructor() {
        this.oTivoSubscription = new TivoSubscriptionModel<SessionState>();
        this.mStreamingSession = new IpLinearStreamingSessionImpl();
    }

    private setKeepAliveInterval = (keepAliveIntervalSeconds: number) => {
        Diagnostics.info(
            SessionManager.TAG,
            `setKeepAliveInterval: keepAliveIntervalSeconds=`,
            keepAliveIntervalSeconds,
        );
        Log.i(
            SessionManager.TAG,
            `setKeepAliveInterval: keepAliveIntervalSeconds=`,
            keepAliveIntervalSeconds,
        );
        clearTimeout(this.timeoutId);
        clearInterval(this.timeoutId);
        this.mStreamingSession.sessionKeepAlive();
        this.timeoutId = setInterval(() => {
            try {
                this.mStreamingSession.sessionKeepAlive();
            } catch (error) {
                clearTimeout(this.timeoutId);
                clearInterval(this.timeoutId);
                this.oTivoSubscription.notify(SessionState.ERROR);
            }
        }, keepAliveIntervalSeconds);
    };

    public async createSession(sessionCreateModel: ISessionCreateModel): Promise<void> {
        clearTimeout(this.timeoutId);
        clearInterval(this.timeoutId);

        if (sessionCreateModel.playbackContentType === PlaybackContentType.LIVE) {
            this.mStreamingSession = new IpLinearStreamingSessionImpl();
        } else if (
            sessionCreateModel.playbackContentType === PlaybackContentType.NDVR_LIVE ||
            sessionCreateModel.playbackContentType === PlaybackContentType.NDVR_RECORDED
        ) {
            this.mStreamingSession = new NdvrStreamingSessionImpl();
        } else {
            this.mStreamingSession = new SocuStreamingSessionImpl();
        }

        try {
            await this.mStreamingSession.createStreamingSession(sessionCreateModel);
            const { firstKeepAliveSeconds, keepAliveIntervalSeconds } = this.getSession();
            this.timeoutId = setTimeout(
                () => {
                    // Time in second converting to millisecond
                    this.setKeepAliveInterval(keepAliveIntervalSeconds * 1000);
                    // Time in second converting to millisecond
                },
                firstKeepAliveSeconds && firstKeepAliveSeconds * 1000,
            );
        } catch (error) {
            Diagnostics.error(SessionManager.TAG, 'session Create SessionManager: ', error);
        } finally {
            this.oTivoSubscription.notify(this.getSessionState());
        }
    }

    public getSession(): Session {
        return this.mStreamingSession.getSessionInfo();
    }

    public getSessionError(): IPlaybackError {
        return this.mStreamingSession.getSessionPlaybackError();
    }

    public getSessionState(): SessionState {
        return this.mStreamingSession.getSessionState();
    }

    public getSessionStateChangeListener(): ITivoSubscription<SessionState> {
        return this.oTivoSubscription;
    }

    public sessionStop(): void {
        this.mStreamingSession.sessionStop();
    }

    public setSessionError(errorType: string): void {
        this.mStreamingSession.setSessionPlaybackError(
            this.mStreamingSession.getPlaybackError(errorType),
        );
    }

    public setSessionState(state: SessionState): void {
        this.mStreamingSession.setSessionState(state);
    }
}
